/**
 * Okta scope banner helpers (POST-ARC.7). Keep in sync with shared/oktaAssessmentReadiness.js.
 *
 * @module client/utils/oktaAssessmentScopeBanner
 */

function getRoleTypesSet(adminRoles) {
  return new Set((adminRoles || []).map((r) => String(r.type || "").toUpperCase()));
}

export function hasFullOrgAdmin(adminRoles) {
  const types = getRoleTypesSet(adminRoles);
  return types.has("SUPER_ADMIN") || types.has("ORG_ADMIN");
}

export function deriveOktaAssessmentReadiness(caps) {
  if (!caps || typeof caps !== "object") {
    return {
      level: "limited",
      summary:
        "Token capabilities unknown — use an Org Administrator or Super Administrator API token for full coverage.",
    };
  }

  const adminRoles = Array.isArray(caps.adminRoles) ? caps.adminRoles : [];
  const readOnlyAdmin = Boolean(caps.readOnlyAdmin);
  const canCreate = caps.canCreate !== false;
  const primaryRoleSummary =
    caps.primaryRoleSummary != null && String(caps.primaryRoleSummary).trim()
      ? String(caps.primaryRoleSummary).trim()
      : "Administrator";

  if (hasFullOrgAdmin(adminRoles)) {
    return {
      level: "full",
      summary: `Full assessment scope (${primaryRoleSummary}).`,
      primaryRoleSummary,
    };
  }

  if (readOnlyAdmin && !canCreate) {
    return {
      level: "read_only",
      summary:
        "Read-only administrator API token — assessments may run, but configuration changes and some admin findings may stay inconclusive.",
      primaryRoleSummary,
    };
  }

  return {
    level: "limited",
    summary: `Limited API token role (${primaryRoleSummary}) — use an Org Administrator or Super Administrator token for full admin and policy coverage.`,
    primaryRoleSummary,
  };
}

export function resolveOktaScopeBanner(capabilities) {
  const readiness = deriveOktaAssessmentReadiness(capabilities);
  if (readiness.level === "full") return null;

  const roleLine = readiness.primaryRoleSummary || "Administrator";

  if (readiness.level === "read_only") {
    return {
      tone: "amber",
      message:
        "Read-only administrator token — assessments can run, but IdWiz cannot apply configuration changes.",
      detail: readiness.summary,
      roleLine,
    };
  }

  return {
    tone: "sky",
    message: `Connected with limited admin scope (${roleLine}).`,
    detail:
      "Some findings (e.g. admin role assignments) may stay inconclusive until you connect with an Org Administrator or Super Administrator API token.",
    roleLine,
  };
}

export function formatOktaConnectSuccessLine(capabilities) {
  if (!capabilities || typeof capabilities !== "object") return "Connected.";
  const name =
    capabilities.name != null && String(capabilities.name).trim()
      ? String(capabilities.name).trim()
      : "user";
  const role =
    capabilities.primaryRoleSummary != null && String(capabilities.primaryRoleSummary).trim()
      ? String(capabilities.primaryRoleSummary).trim()
      : "Administrator";
  return `Connected as ${name} · ${role}`;
}
